import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SIZES = [10, 50, 100];

export function usePagination(items, defaultSize = 10) {
  const [size, setSize] = useState(defaultSize);
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / size));

  // clamp page whenever inputs change
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const slice = useMemo(() => {
    const start = (page - 1) * size;
    return (items || []).slice(start, start + size);
  }, [items, page, size]);

  const controls = {
    page,
    setPage,
    size,
    setSize: (s) => { setSize(s); setPage(1); },
    pageCount,
    total,
  };

  return [slice, controls];
}

export function Pagination({ ctrl, testidPrefix = "pg" }) {
  const { page, setPage, size, setSize, pageCount, total } = ctrl;
  if (total === 0) return null;
  const start = (page - 1) * size + 1;
  const end = Math.min(page * size, total);

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 px-6 py-3 border-t border-border text-xs">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span>Baris per halaman</span>
        <Select value={String(size)} onValueChange={(v) => setSize(Number(v))}>
          <SelectTrigger className="h-8 w-[80px]" data-testid={`${testidPrefix}-size`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-muted-foreground mono">
          {start}–{end} dari {total}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 rounded-sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            data-testid={`${testidPrefix}-prev`}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="mono text-xs px-2" data-testid={`${testidPrefix}-info`}>
            {page} / {pageCount}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 rounded-sm"
            disabled={page >= pageCount}
            onClick={() => setPage(page + 1)}
            data-testid={`${testidPrefix}-next`}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
